import { useSyncExternalStore } from 'react';
import api from '../services/api';
import { getGlobalSocket } from '../hooks/useWebSocket';
import type { LiquidationRisk } from '../hooks/useLiquidationRisk';

/**
 * accountStatsStore — ONE owner of the account-level figures the chrome shows.
 *
 * WHY THIS EXISTS
 *
 * 1. The header's "Updated Ns ago" stamp was lying. `Layout.tsx` ran a bare
 *    `setInterval(() => setLastUpdated(new Date()), 30000)` that fetched NOTHING, so the
 *    freshness indicator advanced on a timer regardless of whether any request had
 *    succeeded — it could never read older than 30s even with the backend down. Meanwhile
 *    the 10s open-P&L poll, the freshest number on the bar, never touched the stamp at all,
 *    and the error branch was unreachable because the three calls ran under
 *    `Promise.allSettled`, which never rejects. A freshness indicator that cannot express
 *    staleness is worse than none: it is an active claim that the numbers beside it are
 *    current.
 *
 *    So `lastSuccessAt` here advances ONLY when a response actually lands. That is the one
 *    property the whole file exists to protect, and `verify:freshness` pins it.
 *
 * 2. The same figures are now wanted in two places at once — the header pill and the chart
 *    title rail. Two components each running their own `useEffect` poll would double the
 *    request rate on an app that has a documented OOM incident from exactly that pattern
 *    (`incident_backend_oom_pollers`). This is a module-level, ref-counted singleton: N
 *    subscribers cost exactly one set of timers, and when the last one unmounts the timers
 *    stop. Same discipline as the backend's `deltaTickerCache`.
 *
 *    It also OWNS the liquidation subscription, and `useLiquidationRisk` now delegates here,
 *    so RiskCockpit + LiquidationRiskBanner + the header + the chart title share one socket
 *    handler and one backstop poll instead of four.
 *
 * NET POLLING CHANGE: two fetches per minute REMOVED (`Layout` used to round-trip
 * `/broker/zerodha/status` and `/broker/delta/status` every 60s to populate state that
 * nothing read — the dots moved to MarketStatusRail long ago). Nothing added.
 *
 * Every loop is `document.hidden`-gated, and a tab returning to the foreground refreshes
 * immediately rather than waiting out the remainder of its interval.
 */

/** The open-positions P&L. The number a trader actually watches, so it gets the fast lane. */
const OPEN_PNL_MS = 10_000;
/** Portfolio value / margin / today's P&L. These do not move at sub-minute granularity. */
const SUMMARY_MS = 60_000;
/** Backstop only — `risk:liquidation` socket events are the live path. */
const RISK_MS = 60_000;

export interface AccountStats {
  /** Unrealised P&L across open LIVE positions, ₹ (paper excluded — audit A-016). */
  openPnl: number | null;
  openCount: number | null;
  /** LIVE baskets the monitor has not priced yet — they add nothing to openPnl. */
  basketsUnpriced: number;
  /** Open PAPER baskets' unrealised P&L, ₹ — shown separately, never summed in. */
  paperPnl: number | null;
  paperCount: number;
  /** Today's realised+unrealised P&L, ₹, from /dashboard/summary. */
  todayPnl: number | null;
  todayPnlPct: number | null;
  portfolioValue: number | null;
  availableMargin: number | null;
  recentAlerts: number;
  risk: LiquidationRisk | null;
  /**
   * Epoch ms of the last SUCCESSFUL response from any loop. Never advanced by a timer,
   * never advanced by a failure. This is what the header stamp renders.
   */
  lastSuccessAt: number | null;
  lastFailureAt: number | null;
  /** True when the most recent attempt failed AND nothing has succeeded since. */
  failing: boolean;
}

/** One explanation for the Open P&L figure, shared by the header pill and the Charts rail. */
export function openPnlExplain(a: Pick<AccountStats, 'openPnl' | 'openCount' | 'basketsUnpriced' | 'paperPnl' | 'paperCount'>, fmt: (v: number) => string): string {
  const parts = [`Unrealised P&L of your LIVE (real-money) open positions${a.openCount != null ? ` — ${a.openCount} open` : ''}.`];
  if (a.openPnl != null) parts.push(`Exact: ${fmt(a.openPnl)}.`);
  if (a.basketsUnpriced > 0) parts.push(`${a.basketsUnpriced} live basket${a.basketsUnpriced > 1 ? 's are' : ' is'} not priced yet and add nothing until the monitor's next cycle.`);
  parts.push(a.paperCount > 0 && a.paperPnl != null
    ? `Paper is not included: ${a.paperCount} paper position${a.paperCount > 1 ? 's' : ''} (baskets and chart trades) at ${fmt(a.paperPnl)}.`
    : 'Paper positions are not included.');
  return parts.join(' ');
}

const EMPTY: AccountStats = {
  openPnl: null, openCount: null, basketsUnpriced: 0, paperPnl: null, paperCount: 0, todayPnl: null, todayPnlPct: null,
  portfolioValue: null, availableMargin: null, recentAlerts: 0, risk: null,
  lastSuccessAt: null, lastFailureAt: null, failing: false,
};

let snapshot: AccountStats = EMPTY;
const listeners = new Set<() => void>();

/** Replace the snapshot immutably so useSyncExternalStore sees a new reference. */
function patch(next: Partial<AccountStats>) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach(fn => { try { fn(); } catch { /* a bad subscriber must not stop the rest */ } });
}

function markSuccess(extra: Partial<AccountStats>) {
  patch({ ...extra, lastSuccessAt: Date.now(), failing: false });
}
function markFailure() {
  patch({ lastFailureAt: Date.now(), failing: true });
}

const num = (v: any): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

// ── fetchers ────────────────────────────────────────────────────────────────────────────
// Each one leaves existing values alone on failure. A blank figure reads as "nothing to
// worry about", which is the failure mode the liquidation guard exists to prevent; the
// stamp going stale is the honest signal, not a cleared number.

async function fetchOpenPnl() {
  try {
    const r = await api.get<{ success: boolean; data: { openPnl: number; openCount: number; basketsUnpriced?: number; paperPnl?: number; paperCount?: number } }>(
      '/dashboard/open-pnl', { headers: { _suppressToast: 'true' } });
    const d = r?.data?.data;
    if (d && typeof d.openPnl === 'number') {
      markSuccess({
        openPnl: d.openPnl, openCount: typeof d.openCount === 'number' ? d.openCount : null,
        basketsUnpriced: Number(d.basketsUnpriced) || 0,
        paperPnl: typeof d.paperPnl === 'number' ? d.paperPnl : null,
        paperCount: Number(d.paperCount) || 0,
      });
    }
  } catch { markFailure(); }
}

async function fetchSummary() {
  try {
    const r = await api.get<any>('/dashboard/summary', { headers: { _suppressToast: 'true' } });
    const d = r?.data?.data || r?.data || {};
    const p = d.portfolio || {};
    const today = d?.pnl?.today || {};
    markSuccess({
      todayPnl: num(today.amount),
      todayPnlPct: num(today.percentage),
      portfolioValue: num(p.totalValue),
      availableMargin: num(p.availableMargin),
      recentAlerts: (d.recentAlerts?.length ?? d.recent_alerts?.length ?? 0) as number,
    });
  } catch { markFailure(); }
}

async function fetchRisk() {
  try {
    const r = await api.get<{ success: boolean; data: LiquidationRisk }>(
      '/dashboard/liquidation-risk', { headers: { _suppressToast: 'true' } });
    const payload = r?.data?.data;
    if (payload) markSuccess({ risk: payload });
  } catch { markFailure(); }
}

// ── lifecycle ───────────────────────────────────────────────────────────────────────────

let refCount = 0;
let timers: number[] = [];
let sock: ReturnType<typeof getGlobalSocket> = null;
let attachTimer: number | undefined;

const onRiskEvent = (data: any) => {
  if (!data) return;
  // Socket events carry the evaluation itself, so `available` is implied.
  patch({ risk: { available: true, ...data }, lastSuccessAt: Date.now(), failing: false });
};

/** Run `fn` only when the tab is visible. A hidden tab costs nothing. */
const whenVisible = (fn: () => void) => () => { if (!document.hidden) fn(); };

const refreshAll = () => { void fetchOpenPnl(); void fetchSummary(); void fetchRisk(); };

const onVisibility = () => { if (!document.hidden) refreshAll(); };

function start() {
  refreshAll();
  timers.push(window.setInterval(whenVisible(fetchOpenPnl), OPEN_PNL_MS));
  timers.push(window.setInterval(whenVisible(fetchSummary), SUMMARY_MS));
  timers.push(window.setInterval(whenVisible(fetchRisk), RISK_MS));
  document.addEventListener('visibilitychange', onVisibility);

  // The global socket is created by useWebSocket and may not exist on first paint.
  // Retry until it does, the same way AlertsList and the old hook did.
  const attach = () => {
    const s = getGlobalSocket();
    if (!s) { attachTimer = window.setTimeout(attach, 300); return; }
    sock = s;
    s.on('risk:liquidation', onRiskEvent);
  };
  attach();
}

function stop() {
  timers.forEach(t => window.clearInterval(t));
  timers = [];
  document.removeEventListener('visibilitychange', onVisibility);
  if (attachTimer) { window.clearTimeout(attachTimer); attachTimer = undefined; }
  if (sock) { sock.off('risk:liquidation', onRiskEvent); sock = null; }
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  if (++refCount === 1) start();
  return () => {
    listeners.delete(fn);
    if (--refCount === 0) stop();
  };
}

const getSnapshot = () => snapshot;

/**
 * Subscribe to the shared account figures. Safe to call from any number of components —
 * they all read the same snapshot and cost one set of timers between them.
 */
export function useAccountStats(): AccountStats {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Force an immediate refresh of every loop (the header's manual retry affordance). */
export function refreshAccountStats() { refreshAll(); }

/* Exported for `verify:freshness`, which asserts that a failed poll cannot advance the
   freshness stamp — the exact property the deleted 30s timer violated. */
export const __testing = {
  reset() { snapshot = EMPTY; },
  get snapshot() { return snapshot; },
  markSuccess, markFailure,
};
